/* External */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  memo,
} from 'react';
import PropTypes from 'prop-types';
import { Map, List, OrderedMap } from 'immutable';
import { FormattedMessage } from 'react-intl';
import { withFocusable } from '@noriginmedia/react-spatial-navigation';
import { useLocation } from 'react-router-dom';

/* Components */
import EpgEmptyChannelMessage from './EpgEmptyChannelMessage';
import EpgFocusedDetail from './EpgFocusedDetail';
import EpgFocusedDetailSkeleton from './EpgFocusedDetailSkeleton';
import EpgRow from './EpgRow';
import FilterList from '../FilterList';
import ChannelLaneSkeleton from '../ChannelLaneSkeleton';
import ChannelsContainer from './ChannelsContainer';
import VirtualizedList, { ItemDataProvider } from '../VirtualizedList';

/* Providers */
import { useCurrentTime } from '../../providers/CurrentTimeProvider';
import { useGtmLocalContext } from '../../providers/GtmLocalProvider';

/* Hooks */
import useCurrentSchedule from '../../hooks/useCurrentSchedule';
import useFetchAvailableSchedules from '../../hooks/useFetchAvailableSchedules';
import useHandleThrottleArrowPress from '../../hooks/useHandleThrottleArrowPress';
import useSortAndFilterChannels from '../../hooks/useSortAndFilterChannels';

/* Utils */
import { COMPONENTS } from '../../utils/focusKey';
import { Direction } from '../../utils/spatialNavigation';
import { getVerticalVisibility } from '../../utils/dom';
import { noOp, stopPropagation } from '../../utils/functions';
import { sizeByViewport } from '../../utils/cardsUtils';
import { useScrollIntoView, ScrollPosition } from '../ScrollView';
import { EPG_FILTERS_ID } from '../../utils/constants';
import { isPlayingNow, isSports } from '../../utils/metaDataUtils';
import {
  formatContentData, formatTimeToStart, getLiveStartProgressBar,
  getParentLander,
  getTagEventFromRoute,
  GTM_CATEGORIES,
  GTM_EVENT_NAMES,
  GTM_SUBCATEGORIES,
  playerType,
  transmissionContentType,
} from '../../utils/googleTags';

/* Styles */
import './Epg.scss';

const epgHeight = sizeByViewport(665);
const CHANNEL_LANE_HEIGHT = sizeByViewport(170);
const SCHEDULES_NUMBER_EPG = 2;

function Epg({
  className,
  channels,
  onLeftArrowPress,
  pageId,
  sourceProvider,
  isLoading,
  isLoadingPage,
  onFetchSchedulesComplete,
  initialScrollPosition,
  setInitialScrollPosition,
  filters,
  selectedFilterId,
  onFilterSelect,
  emptyChannelsMessage,
  hasFocusedChild,
  setFocus,
  withoutFilter,
  biggerChannelLogos,
  readyToRestoreFocus,
  isRecentlyWatchedFetched,
}) {
  const { pathname, state: useLocationState } = useLocation();
  const { commonTaggingEvent } = useGtmLocalContext();
  const currentTime = useCurrentTime();
  const [initialSectionIndexEpg, setInitialSectionIndexEpg] = useState(() => Number.parseFloat(sessionStorage.getItem('initialSectionIndexEpg') || 0));
  const [initialMaxOffset] = useState(() => Number.parseFloat(sessionStorage.getItem('offsetEpg')) || 0);
  const handleThrottleArrowPress = useHandleThrottleArrowPress();
  const areChannelsInDefaultOrder = (selectedFilterId === EPG_FILTERS_ID.RECENTLY_WATCHED
    || selectedFilterId === EPG_FILTERS_ID.MOST_WATCHED);

  const sortedChannels = useSortAndFilterChannels(channels, areChannelsInDefaultOrder);
  const {
    isFetchingChannels,
    isFetchingSchedules,
  } = useFetchAvailableSchedules({
    channels: sortedChannels,
    numberOfSchedules: SCHEDULES_NUMBER_EPG,
    sourceProvider,
  });

  const channelsToRender = useMemo(() => sortedChannels?.toList()
    ?.filter((channel) => !!channel?.schedules), [sortedChannels]);

  const firstChannel = useMemo(() => channelsToRender?.get(0), [channelsToRender]);

  const { schedule: firstChannelCurrentSchedule } = useCurrentSchedule(firstChannel);
  const [focusedSchedule, setFocusedSchedule] = useState(undefined);
  const selectedFilterIdRef = useRef(selectedFilterId);

  useEffect(() => {
    if (selectedFilterIdRef.current !== selectedFilterId) {
      setFocusedSchedule(undefined);
      selectedFilterIdRef.current = selectedFilterId;
    }
  }, [selectedFilterId]);

  useEffect(() => {
    if (!isLoading && !isFetchingChannels && !isFetchingSchedules && onFetchSchedulesComplete) {
      onFetchSchedulesComplete();
    }
  }, [isFetchingSchedules, isFetchingChannels, isLoading, onFetchSchedulesComplete]);

  const [showGradient, setShowGradient] = useState(false);
  const epgRef = useRef(undefined);

  const scrollIntoView = useScrollIntoView();

  const handleScrollPosition = useCallback(
    () => {
      const verticalVisibility = epgRef.current && getVerticalVisibility(epgRef.current);
      // isRecentlyWatchedFetched is a Boolean when it exists in carousels
      const hasRecentlyWatchedAndIsFetched = typeof (isRecentlyWatchedFetched) === 'boolean' ? isRecentlyWatchedFetched : true;
      if (hasRecentlyWatchedAndIsFetched && hasFocusedChild && verticalVisibility < 1) {
        scrollIntoView(epgRef.current, { position: ScrollPosition.START });
      }
    },
    [hasFocusedChild, scrollIntoView, isRecentlyWatchedFetched],
  );

  const [shouldStealFocus, setShouldStealFocus] = useState(!!initialSectionIndexEpg);

  if (sessionStorage.getItem('parentalBlockedChannelId') && epgRef.current) {
    const parentalBlockedChannelIndex = channelsToRender?.findIndex((channel) => channel.channelId === sessionStorage.getItem('parentalBlockedChannelId'));
    if (parentalBlockedChannelIndex >= 0) {
      scrollIntoView(epgRef.current, { position: ScrollPosition.START });
      const newInitialScrollPosition = ((parentalBlockedChannelIndex) * CHANNEL_LANE_HEIGHT);

      setInitialSectionIndexEpg(parentalBlockedChannelIndex + 1);
      setShouldStealFocus(true);
      setInitialScrollPosition(newInitialScrollPosition);

      sessionStorage.setItem('offsetEpg', newInitialScrollPosition);
    }
    sessionStorage.setItem('parentalBlockedChannelId', '');
  }

  const handleListPosition = useCallback((index) => index + 1, []);

  const epgCommonTagEvent = useCallback(({ eventName, extraData }) => {
    const content = extraData?.content;
    const tab = useLocationState?.parentLander
      || getParentLander(getTagEventFromRoute({ path: pathname }));
    const { startProgressBar, duration } = getLiveStartProgressBar({
      startTime: extraData?.startTime,
      endTime: extraData?.endTime,
      currentTime,
    });
    const durationInSeconds = duration * 60;
    commonTaggingEvent({
      category: GTM_CATEGORIES.EPG_DATA,
      subCategory: GTM_SUBCATEGORIES.EPG.SIMPLIFIED_EPG,
      eventName,
      data: {
        content: {
          ...formatContentData({
            content,
            rest: {
              channelId: extraData?.rest?.channelId,
              channelName: extraData?.rest?.channelName,
              isLive: true,
              progress: startProgressBar || 0,
              startProgressBar: startProgressBar || 0,
              duration: durationInSeconds,
            },
          }),
        },
        filterEpg: extraData?.filterId,
        transmissionType: 'live' || transmissionContentType(content?.type || content?.schema),
        playerType: playerType(content?.type, true, isSports(content)),
        isSports: isSports(content),
        isLive: true,
        listPosition: extraData?.listPosition,
        tab,
        timeToStart:
          isPlayingNow(content, currentTime)
            ? 0
            : formatTimeToStart({ startTime: extraData?.startTime }),
      },
    });
  }, [commonTaggingEvent, currentTime, pathname, useLocationState?.parentLander]);

  const handleFilterCategoryEvent = useCallback((filterId, index) => {
    if (filterId) {
      epgCommonTagEvent({
        eventName: GTM_EVENT_NAMES.EPG.FILTER_CATEGORY,
        extraData: { filterId, listPosition: handleListPosition(index) },
      });
    }
  }, [epgCommonTagEvent, handleListPosition]);

  const handleFilterSelect = useCallback(
    (filterId, index) => {
      handleScrollPosition();
      onFilterSelect(filterId);
      handleFilterCategoryEvent(filterId, index);
    },
    [handleFilterCategoryEvent, handleScrollPosition, onFilterSelect],
  );

  const handleChannelBecameFocused = useCallback(
    (position, { schedule, isCurrent = false }) => {
      setFocusedSchedule({ schedule, isCurrent });
      handleScrollPosition();

      if (position >= 2) {
        setShowGradient(true);
      } else {
        setShowGradient(false);
      }
    },
    [handleScrollPosition],
  );

  useEffect(() => {
    handleScrollPosition();
  }, [handleScrollPosition]);

  const handleScroll = useCallback((scrollOffset) => {
    sessionStorage.setItem('offsetEpg', scrollOffset);
    setShowGradient(scrollOffset > 0);
  }, []);

  const handleChannelArrowPress = useCallback((direction, scheduleProps, detail) => {
    if (direction !== Direction.UP && direction !== Direction.DOWN) {
      if (!scheduleProps?.isCurrent) return true;
      if (!filters) {
        return onLeftArrowPress(direction, scheduleProps, detail);
      }
      if (direction === Direction.LEFT) {
        setFocus(`${COMPONENTS.EPG_FILTER_LIST}${selectedFilterId}`);
        return false;
      }
    }
    return handleThrottleArrowPress();
  }, [handleThrottleArrowPress, filters, selectedFilterId, onLeftArrowPress, setFocus]);

  const handleFocusStolen = useCallback(() => {
    if (filters) {
      setShouldStealFocus(false);
    }
  }, [filters]);

  const itemData = useMemo(() => ({
    channels: channelsToRender,
    handleChannelArrowPress,
    handleChannelBecameFocused,
    pageId,
    isFetchingSchedules,
    initialSectionIndexEpg,
    shouldStealFocus: readyToRestoreFocus && shouldStealFocus,
    onFocusStolen: handleFocusStolen,
    withoutFilter,
    biggerChannelLogos,
    epgCommonTagEvent,
  }), [
    handleChannelArrowPress,
    handleChannelBecameFocused,
    isFetchingSchedules,
    pageId,
    channelsToRender,
    initialSectionIndexEpg,
    shouldStealFocus,
    handleFocusStolen,
    withoutFilter,
    biggerChannelLogos,
    readyToRestoreFocus,
    epgCommonTagEvent,
  ]);

  const showSkeletons = (isFetchingChannels || isLoading || isFetchingSchedules);

  const channelsToShow = useMemo(() => (showSkeletons ? (
    <div className="dtv-html5-c-epg__channels-container">
      <ChannelLaneSkeleton withoutFilter={withoutFilter} />
      <ChannelLaneSkeleton withoutFilter={withoutFilter} />
      <ChannelLaneSkeleton withoutFilter={withoutFilter} />
      <ChannelLaneSkeleton withoutFilter={withoutFilter} />
      <ChannelLaneSkeleton withoutFilter={withoutFilter} />
    </div>
  ) : (
    <ItemDataProvider itemData={itemData}>
      <VirtualizedList
        outerElementType={ChannelsContainer}
        height={epgHeight}
        itemCount={channelsToRender?.size}
        itemSize={CHANNEL_LANE_HEIGHT}
        renderer={EpgRow}
        onScroll={handleScroll}
        initialScrollPosition={initialScrollPosition}
        initialMaxOffset={initialMaxOffset}
      />
    </ItemDataProvider>
  )), [
    channelsToRender?.size,
    handleScroll,
    initialMaxOffset,
    initialScrollPosition,
    itemData,
    showSkeletons,
    withoutFilter,
  ]);

  const { schedule, isCurrent = true } = focusedSchedule
    ?? { schedule: firstChannelCurrentSchedule };

  const scheduleChannel = useMemo(() => (
    channels?.find((channel) => channel?.channelId === schedule?.channelId)
  ), [channels, schedule?.channelId]);

  if (
    !isFetchingChannels
      && !filters
      && (!sortedChannels || sortedChannels.size === 0)
  // eslint-disable-next-line unicorn/no-null -- React doesn't support returning undefined
  ) return null;

  return (
    <section
      className={`dtv-html5-c-epg ${
        withoutFilter ? 'dtv-html5-c-epg--without-filter' : ''
      } ${className}`}
    >
      <h1 className="dtv-html5-c-epg__title">
        <FormattedMessage id="epg.title" />
      </h1>
      <div ref={epgRef}>
        {!!schedule && channelsToRender?.size > 0 && !showSkeletons ? (
          <EpgFocusedDetail
            schedule={schedule}
            isCurrent={isCurrent}
            channel={scheduleChannel}
          />
        ) : (
          <EpgFocusedDetailSkeleton emptyMessage={!showSkeletons && !channelsToRender?.size} />
        )}
        <div className="dtv-html5-c-epg__container">
          {filters && (
            <FilterList
              filters={filters}
              selectedFilterId={selectedFilterId}
              onFilterSelect={handleFilterSelect}
              focusable={!isLoadingPage && !shouldStealFocus}
            />
          )}
          <div className="dtv-html5-c-epg__fixed-container">
            <div
              className={`dtv-html5-c-epg__subtitles-container ${
                showGradient ? 'dtv-html5-c-epg__subtitles-container--show-gradient' : ''
              }`}
              onWheel={stopPropagation}
            >
              <div className="dtv-html5-c-epg__live-tag">
                <FormattedMessage id="common.live" />
              </div>
              <div className="dtv-html5-c-epg__next">
                <FormattedMessage id="epg.comingUp" />
              </div>
            </div>
            {!showSkeletons && !channelsToRender?.size && emptyChannelsMessage
              ? (
                <EpgEmptyChannelMessage
                  emptyChannelsMessage={emptyChannelsMessage}
                />
              ) : (
                channelsToShow
              )}
          </div>
        </div>
      </div>
    </section>
  );
}

Epg.propTypes = {
  className: PropTypes.string,
  channels: PropTypes.oneOfType([
    PropTypes.instanceOf(Map),
    PropTypes.instanceOf(List),
  ]),
  onLeftArrowPress: PropTypes.func,
  pageId: PropTypes.string,
  sourceProvider: PropTypes.string,
  isLoading: PropTypes.bool,
  isLoadingPage: PropTypes.bool,
  onFetchSchedulesComplete: PropTypes.func,
  initialScrollPosition: PropTypes.number,
  setInitialScrollPosition: PropTypes.func,
  onFilterSelect: PropTypes.func,
  filters: PropTypes.instanceOf(OrderedMap),
  selectedFilterId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  emptyChannelsMessage: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
  }),
  hasFocusedChild: PropTypes.bool.isRequired,
  setFocus: PropTypes.func.isRequired,
  withoutFilter: PropTypes.bool,
  biggerChannelLogos: PropTypes.bool,
  readyToRestoreFocus: PropTypes.bool,
  isRecentlyWatchedFetched: PropTypes.bool,
};

Epg.defaultProps = {
  className: '',
  channels: undefined,
  onLeftArrowPress: noOp,
  pageId: '',
  sourceProvider: '',
  isLoadingPage: false,
  isLoading: false,
  onFetchSchedulesComplete: undefined,
  filters: undefined,
  initialScrollPosition: 0,
  setInitialScrollPosition: undefined,
  selectedFilterId: undefined,
  onFilterSelect: noOp,
  emptyChannelsMessage: undefined,
  withoutFilter: false,
  biggerChannelLogos: false,
  readyToRestoreFocus: true,
  isRecentlyWatchedFetched: undefined,
};

export default memo(withFocusable({ trackChildren: true })(Epg));
